import { ButtonGroup, Classes, Colors, Dialog, Intent } from "@blueprintjs/core";
import { ButtonContainer, FieldSet, IDataTableColumn, useGridState } from "nsitools-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { DogBaseSearch, DogOwnerApi, DogOwnerGridDto } from "../../../../../../api";
import { ERoutes } from "../../../../../../ERoutes";
import { CustomDataTable, DeleteButton, EditButton, EmailButton, ExportButton } from "../../../../../../components";
import { useGlobalData } from "../../../../../../contexts";
import { useApiService, useDeleteApi, useSearchApiWithLanguageRefresh, useTl } from "../../../../../../hooks";
import { ETLCodes } from "../../../../../../locales";
import { generateAddress } from "../../../../../../utils";
import { DogOwnerPanelStack } from "./DogOwnersPanelStack";

export interface IDogOwnerListPageProps {
  dogId: number;
  editMode?: boolean;
  dogBirthDate: Date;
}

export const DogOwnerListPage: React.FunctionComponent<IDogOwnerListPageProps> = ({
  dogId,
  editMode = false,
  dogBirthDate,
}) => {
  const { t } = useTl();
  const api = useApiService(DogOwnerApi);
  const { currentLanguage } = useGlobalData();
  const [dialogOpened, setDialogOpened] = React.useState(false);
  const [sobjState, setsobjState] = React.useState<any>();
  const [dogOwnershipId, setdogOwnershipId] = React.useState<number>(0);
  const tableState = useGridState<any>({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [5, 10, 15],
    pageSize: 5,
  });

  const searchFunction = React.useCallback(
    (sObj?) => {
      const s = DogBaseSearch.fromJS({
        ...sObj,
        dogId: dogId,
        languageId: currentLanguage.languageId,
      });
      setsobjState(s);
      return api.baseSearch(s);
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApiWithLanguageRefresh({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });
  const { deleteItem } = useDeleteApi<number>(
    React.useMemo(
      () => ({
        deleteApiFn: (data) => api.deleteOwner(data),
        onDeletedFn: () => search(),
      }),
      [api, search]
    )
  );

  const editOwnership = React.useCallback((ownershipId: number) => {
    setdogOwnershipId(ownershipId);
    setDialogOpened(true);
  }, []);

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        computed: true,
        disableSorting: true,
        fieldName: "actions",
        autoFitContent: true,
        hidden: !editMode,
        render: (row: DogOwnerGridDto) => (
          <ButtonContainer>
            <EditButton minimal={true} onClick={() => editOwnership(row.dogOwnershipId)} />
            <DeleteButton minimal={true} onDelete={() => deleteItem(row.dogOwnershipId)} />
          </ButtonContainer>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        fieldName: "info",
        autoFitContent: true,
        render: (row: DogOwnerGridDto) => {
          if (row.isTemporaryOwner) {
            return <i className="fas fa-hourglass-half" />;
          }
        },
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Name),
        fieldName: "name",
        render: (row: DogOwnerGridDto) => (
          <Link target="_blank" to={`${ERoutes.people}/${row.personId}/detail/view`}>
            {row.computedName}
          </Link>
        ),
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.OwnerFrom),
        fieldName: "ownerFrom",
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.OwnerTo),
        fieldName: "ownerTo",
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.Email),
        fieldName: "email",
        render: (value: DogOwnerGridDto) => <EmailButton email={value.email} />,
      },
      {
        computed: true,
        disableSorting: true,
        header: () => t(ETLCodes.MailAddress),
        fieldName: "address",
        render: (row: DogOwnerGridDto) =>
          generateAddress(row.number, row.street, row.city, row.postalCode, row.country),
      },
    ],
    [deleteItem, editMode, editOwnership, t]
  );

  const openDialog = React.useCallback(() => {
    setDialogOpened(true);
  }, []);
  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <FieldSet
        title={t(ETLCodes.Owners)}
        rightElement={
          <ButtonGroup>
            <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sobjState} />
            {editMode && (
              <EditButton
                onClick={(e) => {
                  e.stopPropagation();
                  openDialog();
                }}
                text={t(ETLCodes.General_Add)}
                intent={Intent.PRIMARY}
              />
            )}
          </ButtonGroup>
        }
      >
        <CustomDataTable tableState={tableState} loading={loading} columns={columns} />
      </FieldSet>

      <Dialog
        title={t(ETLCodes.OwnerSelection)}
        isOpen={dialogOpened}
        onClose={() => {
          setDialogOpened(false);
          setdogOwnershipId(null);
        }}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        isCloseButtonShown={true}
        className={Classes.DIALOG}
        style={{ width: 1050, height: 550, background: Colors.WHITE }}
      >
        <div className={Classes.DIALOG_BODY}>
          <DogOwnerPanelStack
            onSaved={() => {
              setDialogOpened(false);
              search();
            }}
            dogId={dogId}
            dogBirthDate={dogBirthDate}
            dogOwnershipId={dogOwnershipId}
          />
        </div>
      </Dialog>
    </>
  );
};
